import {
  buildWhereParams,
  storeApiResources,
  type ApiWhere,
} from '@eliadmin/core/src/api/store-list-request';
import { createContextStore } from '@eliadmin/core/src/stores/context-store';
import { useEffect, useRef } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { courseFilterParams, parseCourseFilters } from './course-list-url';

// Define the JobportalCoursesFilter type
export type JobportalCoursesFilter = {
  search?: string;
  age_min?: string;
  age_max?: string;
  is_paid?: boolean;
  categories?: string[];
  min_salary?: string;
  max_salary?: string;
  employment_types?: string[];
  languages?: string[];
  driving_licenses?: string[];
  salary_types?: string[];
  places_of_work?: string[];
  regions?: string[];
  educations?: string[];

  order?: 'asc' | 'desc';
  orderBy?: 'created_at' | 'salary' | 'distance';
};

// Define the store state
type JobportalCoursesStoreState = {
  filter: JobportalCoursesFilter;
  autoUpdateSearchParams: boolean;
  clearTrigger: boolean;
  salaryRanges?: {
    '1': { min: string; max: string };
    '2': { min: string; max: string };
    overall: { min: string; max: string };
  };
};

// Define the store actions
type JobportalCoursesStoreActions = {
  clearFilters: () => void;
  setFilter: (filter: Partial<JobportalCoursesFilter>) => void;
  replaceFilter: (filter: JobportalCoursesFilter) => void;
  setAutoUpdateSearchParams: (value: boolean) => void;
  applyFilter: (filter: JobportalCoursesFilter) => void;
};

// Create the context store
const [JobportalCoursesStoreProvider, useJobportalCoursesStore] = createContextStore<
  JobportalCoursesStoreState,
  JobportalCoursesStoreActions
>(
  {
    filter: {},
    autoUpdateSearchParams: true,
    clearTrigger: false,
  },
  (set, get) => ({
    clearFilters: () => {
      set(state => {
        state.filter = parseCourseFilters(new URLSearchParams());
        state.clearTrigger = !state.clearTrigger;
      });
    },
    setFilter: filter => {
      set(state => {
        state.filter = {
          ...state.filter,
          ...filter,
        };
      });
    },
    replaceFilter: filter =>
      set(state => {
        state.filter = filter;
      }),
    setAutoUpdateSearchParams: value => {
      set(state => {
        state.autoUpdateSearchParams = value;
      });
    },
    applyFilter: filter => {
      const pathname = window.location.pathname;
      const merged = { ...get().filter, ...filter };
      set(state => {
        state.filter = merged;
      });
      const searchString = courseFilterParams(merged, window.location.search);
      window.history.replaceState({}, '', `${pathname}${searchString ? `?${searchString}` : ''}`);
    },
  }),
);

// JobportalCoursesFilterParamsSync component
function JobportalCoursesFilterParamsSync() {
  const filter = useJobportalCoursesStore(state => state.filter);
  const autoUpdateSearchParams = useJobportalCoursesStore(state => state.autoUpdateSearchParams);
  const replaceFilter = useJobportalCoursesStore(state => state.replaceFilter);

  const pathname = usePathname();
  const searchParams = useSearchParams();

  const url = searchParams.toString();
  const serialized = courseFilterParams(filter);
  const previous = useRef({ url, serialized });

  // URL is the request source of truth. Initial page=2 must survive mounting;
  // back/forward replaces filters, while an actual UI filter change resets page.
  useEffect(() => {
    if (url !== previous.current.url) {
      const incoming = parseCourseFilters(new URLSearchParams(url));
      previous.current = { url, serialized: courseFilterParams(incoming) };
      if (serialized !== previous.current.serialized) replaceFilter(incoming);
      return;
    }
    if (!autoUpdateSearchParams || serialized === previous.current.serialized) return;
    const searchString = courseFilterParams(filter, url);
    previous.current = { url: searchString, serialized };
    window.history.replaceState({}, '', `${pathname}${searchString ? `?${searchString}` : ''}`);
  }, [filter, serialized, url, pathname, autoUpdateSearchParams, replaceFilter]);

  return null;
}

// Export the components
export {
  JobportalCoursesStoreProvider,
  useJobportalCoursesStore,
  JobportalCoursesFilterParamsSync,
};

export const buildCoursesQuery = (params: JobportalCoursesFilter) => {
  const where: ApiWhere<typeof storeApiResources.storefrontCourseCourses> = {};
  // Keep the existing UI convention: false means no paid-only constraint.
  if (params.is_paid) where.isPaid = { equals: params.is_paid };
  if (params.categories?.length) where.categoriesId = { in: params.categories };
  return {
    // Callers spread this after URL query params; keep the old builder's filter precedence.
    filter: undefined,
    ...(Object.keys(where).length
      ? buildWhereParams(where, storeApiResources.storefrontCourseCourses)
      : {}),
  };
};
